import React, { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, WifiOff, Server } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServerStatus = ({ children }) => {
  const [serverDown, setServerDown] = useState(false);
  const [checking, setChecking] = useState(false);
  const [errorType, setErrorType] = useState('server'); // 'server' or 'network'

  const checkServerHealth = async () => {
    setChecking(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      // Try the API health endpoint
      const response = await fetch(`${API_URL}/api/health`, {
        method: 'GET',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        setServerDown(false);
        setErrorType('server');
      } else {
        // If /api/health fails, try a simpler check - login page
        const response2 = await fetch(`${API_URL}/api/auth/health`, {
          method: 'GET',
          signal: AbortSignal.timeout(5000)
        }).catch(() => null);
        
        if (response2?.ok) {
          setServerDown(false);
        } else {
          setServerDown(true);
          setErrorType('server');
        }
      }
    } catch (error) {
      setServerDown(true);
      if (error.name === 'AbortError' || !navigator.onLine) {
        setErrorType('network');
      } else {
        setErrorType('server');
      }
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    // Check server health on mount
    checkServerHealth();

    // Also listen for online/offline events
    const handleOnline = () => checkServerHealth();
    const handleOffline = () => {
      setServerDown(true);
      setErrorType('network');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (serverDown) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          {/* Icon */}
          <div className="mb-6">
            {errorType === 'network' ? (
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                <WifiOff className="w-10 h-10 text-orange-500" />
              </div>
            ) : (
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <Server className="w-10 h-10 text-red-500" />
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-800 mb-3">
            {errorType === 'network' ? 'No Internet Connection' : 'Server Temporarily Unavailable'}
          </h1>

          {/* Message */}
          <p className="text-gray-600 mb-6">
            {errorType === 'network' ? (
              <>
                Your internet connection has been lost. Please check your network connection and try again.
              </>
            ) : (
              <>
                We're experiencing some technical difficulties. Our team is working to fix this. Please try again in a few moments.
              </>
            )}
          </p>

          {/* Warning Box */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <p className="text-sm text-yellow-800 font-medium">
                  {errorType === 'network' ? 'Network Error' : 'Server Maintenance'}
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  {errorType === 'network' 
                    ? 'Please check your WiFi or mobile data connection'
                    : 'Our team has been notified and is working on it'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Retry Button */}
          <Button
            onClick={checkServerHealth}
            disabled={checking}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            {checking ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </>
            )}
          </Button>

          {/* Support Info */}
          <p className="text-xs text-gray-500 mt-6">
            If the problem persists, please contact support
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default ServerStatus;
